import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as fromDatabaseActions from '../actions/database.action';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { setLoadingSpinner } from '../actions/shared.action';
import { ModalService } from 'src/app/services/modal.service';
import { DatabaseService } from 'src/app/services/database.service';
import { ROUTER_NAVIGATION, RouterNavigatedAction } from '@ngrx/router-store';

@Injectable()
export class DatabaseEffects {
  getDatabaseList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r: RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/database');
      }),
      exhaustMap(() =>
        this.databaseService.getDatabaseList().pipe(
          map((response) => {
            return fromDatabaseActions.getDatabaseListSuccess({ data: response });
          }),
          catchError((error) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromDatabaseActions.getDatabaseListFailed({ error: error?.error?.message || 'Unknown error' }));
          })
        )
      )
    )
  );

  reloadDatabase$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromDatabaseActions.getDatabaseList),
      exhaustMap((action) => 
        this.databaseService.getDatabaseList().pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return fromDatabaseActions.getDatabaseListSuccess({data: response})
          }),
          catchError((error)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromDatabaseActions.getDatabaseListFailed({ error: error?.error?.message || 'Unknown error' }));
          })
        )
      )
    )
  );

  getDatabaseDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromDatabaseActions.getDatabaseDetails),
      exhaustMap((action) =>
        this.databaseService.getDatabaseDetails(action.uuid).pipe(
          map((response) => {
            return fromDatabaseActions.getDatabaseDetailsSuccess({ data: response });
          }),
          catchError((error) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromDatabaseActions.getDatabaseDetailsFailed({ error: error?.error?.message || 'Unknown error' }));
          })
        )
      )
    )
  );

  getDatabaseListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDatabaseActions.getDatabaseListSuccess),
        tap(() => {
          this.store.dispatch(setLoadingSpinner({ status: false }));
        })
      ),
    { dispatch: false }
  );

  getDatabaseDetailsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDatabaseActions.getDatabaseDetailsSuccess),
        tap(() => {
          this.store.dispatch(setLoadingSpinner({ status: false }));
        })
      ),
    { dispatch: false }
  );

  getDatabaseListFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDatabaseActions.getDatabaseListFailed),
        tap((action) => {
          this.store.dispatch(setLoadingSpinner({ status: false }));
          const message = action.error || 'Unknown error occurred. Please try again later.';
          this.modalService.errorModal(message);
        })
      ),
    { dispatch: false }
  );

  getDatabaseDetailsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDatabaseActions.getDatabaseDetailsFailed),
        tap((action) => {
          this.store.dispatch(setLoadingSpinner({ status: false }));
          const message = action.error || 'Failed to load resident details. Please try again later.';
          this.modalService.errorModal(message);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private databaseService: DatabaseService,
    private modalService: ModalService
  ) {}
}